<!-- Body -->
<div class="page-container inner-padding" [class.loading]="isLoading">
    <app-header title="{{ 'account.employeeProfile' | translate }}" [menuService]="tabMenuService">
        <div class="buttons">
            <ng-container *ngIf="canCreateChangeRequest">
                <ui-button type="outline" [matMenu]="changeRequestMenu"
                    >{{ 'account.requestChange' | translate }} <mat-icon svgIcon="chevronDown"></mat-icon>
                </ui-button>
                <mat-menu #changeRequestMenu="matMenu">
                    <button
                        *ngFor="let template of changeRequestTemplates"
                        mat-menu-item
                        [routerLink]="['/change-requests', 'templates', template.id, 'employees', employee.id, 'new']"
                    >
                        {{ changeRequestTemplate[template.name] | translate }}
                    </button>
                </mat-menu>
            </ng-container>
        </div>
    </app-header>

    <app-warning-banner
        *ngIf="payrollEnabled && employee.isPayrollSyncEnabled && !!jobAndPayWarningProvider"
        header="There are issues that may prevent you from being paid"
        [warningsToDisplayProvider]="jobAndPayWarningProvider"
        [withMargin]="false"
    ></app-warning-banner>

    <div class="profile-layout">
        <app-profile-card class="profile-layout-sidebar" [model]="employee"></app-profile-card>
        <div class="profile-layout-content">
            <!-- Employment Info -->
            <ui-card class="profile-card" role="group" aria-labelledby="employment-info-header" [withMargin]="false">
                <div class="profile-card-header">
                    <h2 id="employment-info-header">{{ 'account.employmentInformation' | translate }}</h2>
                    <ui-button
                        *ngIf="canShowEditEmploymentInformation"
                        (click)="currentlyOpenDialog = 'EMPLOYMENT_INFO'"
                        type="link"
                        >{{ 'edit' | translate }}</ui-button
                    >
                </div>
                <label>{{ 'account.dateHired' | translate }}</label>
                <div>{{ employee.hiredAt | appDate: 'mediumDate' }}</div>
                <label>{{ 'account.reportsTo' | translate }}</label>
                <div>{{ employee.manager?.fullName }}</div>
                <label>{{ 'account.department' | translate }}</label>
                <div>{{ employee.department?.name }}</div>
                <label>{{ 'account.position' | translate }}</label>
                <div>{{ employee.job?.title }}</div>
                <label>{{ 'account.officeLocation' | translate }}</label>
                <div>{{ employee.office?.fullAddress }}</div>
                <label>{{ 'account.employmentType' | translate }}</label>
                {{
                    (employee.employmentType ? employmentTypesDisplay[employee.employmentType] : '')
                        | translate
                        | capitalize
                }}
                <label>{{ 'account.stockOptions' | translate }}</label>
                <span class="blur"> {{ employee.stockOptions }} </span>
                <label>{{ 'account.additionalTerms' | translate }}</label>
                <div>{{ employee.additionalTerms }}</div>
                <app-data-field-group-values
                    [group]="getDataFieldGroupByName(defaultGroupFieldNames.employment_information, dataFields, true)"
                    [dataFields]="dataFields"
                    [dataValues]="dataValues"
                ></app-data-field-group-values>
            </ui-card>

            <!-- Banking Info -->
            <ui-card
                class="profile-card"
                role="group"
                aria-labelledby="banking-info-header"
                [withMargin]="false"
                [class.loading]="!isBankAccountLoaded"
            >
                <div class="profile-card-header">
                    <h2 id="banking-info-header">{{ 'account.bankAccount' | translate }}</h2>
                    <ui-button (click)="currentlyOpenDialog = 'BANKING_INFO'" type="link">{{
                        'edit' | translate
                    }}</ui-button>
                </div>
                <label>{{ 'account.paymentMethod' | translate }}</label>
                <div>
                    {{ employee.paymentMethod | appTranslateOptions: paymentMethodDisplay }}
                </div>
                <label>{{ 'account.bankInstitution' | translate }}</label>
                <div>{{ employee.bankAccount?.institutionNumber }}</div>
                <label>{{ 'account.bankBranch' | translate }}</label>
                <div>{{ employee.bankAccount?.branchNumber }}</div>
                <label>{{ 'account.bankAccount' | translate }}</label>
                <div>{{ employee.bankAccount?.accountNumber }}</div>
                <app-data-field-group-values
                    [group]="getDataFieldGroupByName(defaultGroupFieldNames.bank_account, dataFields, true)"
                    [dataFields]="dataFields"
                    [dataValues]="dataValues"
                ></app-data-field-group-values>
            </ui-card>

            <!-- Current Compensation -->
            <ui-card
                role="group"
                aria-labelledby="current-comp-header"
                class="profile-card"
                *permission="viewCompensationPermission"
                [withMargin]="false"
                [class.loading]="!isCurrentCompensationsLoaded"
            >
                <div class="profile-card-header">
                    <h2 id="current-comp-header">{{ 'account.currentCompensation' | translate }}</h2>
                </div>
                <ng-container *ngIf="employeeCurrentSalaries?.length; else emptyCurrentCompensationState">
                    <div *ngFor="let salary of employeeCurrentSalaries" class="current-compensation">
                        <label>{{ 'account.compensationName' | translate }}</label>
                        <div>
                            {{
                                salary?.name && salary?.name !== 'Regular Pay'
                                    ? salary.name
                                    : ('account.regularPay' | translate)
                            }}
                        </div>
                        <label>{{ 'account.compensationType' | translate }}</label>
                        <div>
                            {{ (salary?.frequency === 'year' ? 'account.yearly' : 'account.hourly') | translate }}
                        </div>
                        <label>{{ 'account.salaryRate' | translate }}</label>
                        <div class="blur">
                            {{
                                salary?.rate ? (salary.rate | i18nNum: '$') : ('employees.jobAndPay.notSet' | translate)
                            }}
                            {{ employee.currency }}
                        </div>
                        <label>{{ 'account.effectiveDate' | translate }}</label>
                        <div>{{ salary?.effectiveAt ? (salary.effectiveAt | i18nDate: 'short') : '' }}</div>
                        <!-- Vacation Pay -->
                        @if (showVacationPay) {
                            <label>{{ 'account.vacationPay' | translate }}</label>
                            <div>
                                {{
                                    salary?.hasVacationPayPercentage
                                        ? ('account.vacationPayPercentage'
                                          | translate
                                              : {
                                                    percentage: salary.vacationPayPercentage | i18nNum,
                                                })
                                        : ' - '
                                }}
                            </div>
                        }
                        <label>{{ 'account.hoursPerWeek' | translate }}</label>
                        <div>{{ salary?.hoursPerWeek ? (salary.hoursPerWeek | i18nNum) : '-' }}</div>
                    </div>
                </ng-container>
                <ng-template #emptyCurrentCompensationState>
                    <app-empty-state
                        label="{{
                            'account.noCurrentCompensationsToDisplay' | translate: { firstName: employee.firstName }
                        }}"
                        src="/assets/illustrations/piggy-bank.png"
                    >
                    </app-empty-state>
                </ng-template>
            </ui-card>

            <!-- Vacation Pay Method -->
            <ui-card
                class="profile-card"
                role="group"
                aria-labelledby="vacation-header"
                [withMargin]="false"
                *ngIf="employeeVacationPayMethod && payrollEnabled && employee?.activeOnPayroll()"
                [class.loading]="!isCurrentCompensationsLoaded"
            >
                <div class="profile-card-header">
                    <h2 id="vacation-header">{{ 'account.vacationPayMethod' | translate }}</h2>
                </div>
                <div class="vacation-pay">
                    <label>{{ 'account.howToPayOutVacationPay' | translate }}</label>
                    <div>
                        {{
                            (employee.vacationPayMethod === null || employee.vacationPayMethod === 'vacation_accrual'
                                ? 'account.accruedVacationPay'
                                : 'account.payOutAccruedVacationPay'
                            ) | translate
                        }}
                    </div>
                </div>
            </ui-card>

            <!-- Compensation History -->
            <ui-card
                role="group"
                attr.aria-label="{{ 'account.compensationHistory' | translate }}"
                [withSpacing]="false"
                [withMargin]="false"
                *ngIf="displayCompensationTable"
            >
                <app-table
                    [isResponsive]="true"
                    [showOffset]="true"
                    [noPadding]="true"
                    [embedded]="true"
                    [showSearch]="false"
                    [useStickyHeader]="false"
                    [table]="readOnlyTable"
                    title="{{ 'account.compensationHistory' | translate }}"
                >
                    <app-empty-state
                        [label]="!table.hasFiltersApplied() ? 'account.noCompensationsToDisplay' : ''"
                        src="/assets/illustrations/piggy-bank.png"
                    ></app-empty-state>
                </app-table>
            </ui-card>

            <app-data-field-groups
                *ngIf="dataFields && dataValues && isDataFieldsLoaded"
                [dataFields]="dataFields"
                [dataValues]="dataValues"
                [canEditDataValues]="canEditDataValues"
                component="job_and_pay"
                (edit)="onDataFieldEdit($event)"
                [withMargin]="false"
            ></app-data-field-groups>
        </div>
    </div>
</div>

<!-- Modals -->
<ui-dialog
    [open]="currentlyOpenDialog === 'EMPLOYMENT_INFO'"
    [form]="formEmploymentInformation?.form"
    (primaryButtonPressed)="
        submitForm(formEmploymentInformation ?? formEmploymentInformationCustom, !formEmploymentInformation)
    "
    primaryButtonLabel="save"
    (secondaryButtonPressed)="onCancel()"
    secondaryButtonLabel="cancel"
    (closing)="onCancel()"
    header="account.editEmploymentInformation"
    [stickyButtons]="true"
    [stickyHeader]="true"
>
    <app-form-employment-information
        *permission="editEmploymentPermission"
        #formEmploymentInformation
        [employee]="employee"
    >
    </app-form-employment-information>

    <app-form-data-fields
        *ngIf="currentlyOpenDialog === 'EMPLOYMENT_INFO'"
        #formEmploymentInformationCustom
        [employee]="employee"
        [dataFieldGroup]="defaultGroupFieldNames.employment_information"
        [dataFields]="editableDataFields"
        [(dataValues)]="dataValues"
        [isDefaultGroup]="true"
    ></app-form-data-fields>
</ui-dialog>

<ui-dialog
    [open]="currentlyOpenDialog === 'BANKING_INFO'"
    [form]="formBankAccount.form"
    (primaryButtonPressed)="submitForm(formBankAccount)"
    primaryButtonLabel="save"
    (secondaryButtonPressed)="onCancel()"
    secondaryButtonLabel="cancel"
    (closing)="onCancel()"
    header="account.editBankAccount"
    size="small"
    [stickyButtons]="true"
    [stickyHeader]="true"
>
    <app-form-bank-account
        #formBankAccount
        [bankAccount]="bankAccount"
        [employee]="employee"
        [bankingInformationRequiredByDefault]="bankingInformationRequiredByDefault"
    >
    </app-form-bank-account>

    <app-form-data-fields
        *ngIf="currentlyOpenDialog === 'BANKING_INFO'"
        [employee]="employee"
        [dataFieldGroup]="defaultGroupFieldNames.bank_account"
        [dataFields]="editableDataFields"
        [(dataValues)]="dataValues"
        [isDefaultGroup]="true"
    ></app-form-data-fields>
</ui-dialog>

<ui-dialog
    [open]="currentlyOpenDialog === 'CUSTOM_DATA_GROUP'"
    [form]="editDataFieldsForm.form"
    (primaryButtonPressed)="submitForm(editDataFieldsForm, true)"
    primaryButtonLabel="save"
    (secondaryButtonPressed)="onCancel()"
    (closing)="onCancel()"
    [stickyHeader]="true"
    [stickyButtons]="true"
    secondaryButtonLabel="cancel"
    header="edit"
>
    <app-form-data-fields
        #editDataFieldsForm
        [employee]="employee"
        [dataFieldGroup]="editFieldGroup"
        [dataFields]="editableDataFields"
        [(dataValues)]="dataValues"
    ></app-form-data-fields>
</ui-dialog>
